/* eslint-disable jira/js/no-inject-intl-if-functional-component -- go/jfe-eslint */

import React, { type SyntheticEvent, type ReactNode } from 'react';
import { styled as styled2 } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/standard-button';
import WarningIcon from '@atlaskit/icon/glyph/warning';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import ModalDialog, { ModalBody, ModalHeader, ModalFooter } from '@atlaskit/modal-dialog';
import { Inline } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import {
	MODAL,
	ContextualAnalyticsData,
	FireScreenAnalytics,
	fireUIAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { isUser, triggerAnalytics as triggerAnalyticsForApp } from '../../../common/utils';
import { ANALYTICS_REMOVE_MODAL, ANALYTICS_REMOVE_BTN } from './constants';
import messages from './messages';
import type { Props } from './types';

export const RemoveActorDialog = (props: Props) => {
	const {
		isSubmitting,
		actor,
		onConfirm,
		onDismiss,
		hasPermissionsGate,
		intl: { formatMessage },
	} = props;

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const onRemoveActor = (event: SyntheticEvent<any>, analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, ANALYTICS_REMOVE_BTN);
		actor && onConfirm(actor, analyticsEvent);
	};

	const userMsg = hasPermissionsGate
		? formatMessage(messages.userRemovalConfirmationMessagePermissionGated)
		: formatMessage(messages.userRemovalConfirmationMessage, {
				userFullName: actor ? actor.displayName : '',
			});
	const groupMsg = hasPermissionsGate
		? formatMessage(messages.groupRemovalConfirmationMessagePermissionGated)
		: formatMessage(messages.groupRemovalConfirmationMessage, {
				groupName: actor ? actor.displayName : '',
			});
	const dialogMsg = actor && isUser(actor.type) ? userMsg : groupMsg;

	return (
		<ContextualAnalyticsData sourceType={MODAL} sourceName={ANALYTICS_REMOVE_MODAL}>
			<FireScreenAnalytics />

			<ShortcutScope>
				<ModalDialog
					width="small"
					// @ts-expect-error - TS2339 - Property 'keyCode' does not exist on type 'MouseEvent<any, MouseEvent> | KeyboardEvent<any>'.
					onDialogDismissed={(e) => {
						onDismiss();

						if (typeof e !== 'undefined' && e.keyCode === 27) {
							triggerAnalyticsForApp('cancel-remove-actor-dialog', {
								method: 'esc',
							});
						} else {
							triggerAnalyticsForApp('cancel-remove-actor-dialog', {
								method: 'blanket-or-other',
							});
						}
					}}
				>
					<ModalHeader>
						<HeaderWrapper>
							<WarningIconWrapper
								primaryColor={token('color.icon.warning', colors.Y300)}
								label={formatMessage(messages.iconLabel, {
									userOrGroupDisplayName: actor ? actor.displayName : '',
								})}
							/>
							<WarningText>
								{formatMessage(messages.removeActorHeader, {
									userOrGroupDisplayName: actor ? actor.displayName : '',
								})}
							</WarningText>
						</HeaderWrapper>
					</ModalHeader>
					<ModalBody>{dialogMsg}</ModalBody>
					<ModalFooter>
						<Inline space="space.100">
							<div data-testid="users-and-roles.ui.actors-table.remove-actor-dialog.submit">
								<Button
									appearance="warning"
									onClick={onRemoveActor}
									// @ts-expect-error - TS2322 - Type '{ children: string; appearance: "warning"; onClick: (event: SyntheticEvent<any, Event>, analyticsEvent: UIAnalyticsEventInterface) => void; externalId: string; isDisabled: boolean; }' is not assignable to type 'IntrinsicAttributes & ButtonProps & RefAttributes<HTMLElement>'.
									externalId="users-and-roles.submit-remove-actor-dialog"
									isDisabled={isSubmitting}
								>
									{formatMessage(
										hasPermissionsGate
											? messages.removeButtonLabelPermissionGated
											: messages.removeButtonLabel,
									)}
								</Button>
							</div>
							<div data-testid="users-and-roles.ui.actors-table.remove-actor-dialog.cancel">
								<Button
									appearance="subtle-link"
									onClick={onDismiss}
									// @ts-expect-error - TS2322 - Type '{ children: string; appearance: "subtle-link"; onClick: () => void; externalId: string; externalValues: { method: any; }; }' is not assignable to type 'IntrinsicAttributes & ButtonProps & RefAttributes<HTMLElement>'.
									externalId="users-and-roles.cancel-remove-actor-dialog"
									externalValues={{
										method: 'footer-button',
									}}
								>
									{formatMessage(messages.cancelButtonLabel)}
								</Button>
							</div>
						</Inline>
					</ModalFooter>
				</ModalDialog>
			</ShortcutScope>
		</ContextualAnalyticsData>
	);
};

export default injectIntl(RemoveActorDialog);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderWrapper = styled2.h1<{
	children?: ReactNode;
}>({
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& > span': {
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: '20px',
		fontWeight: '500',
		letterSpacing: '-0.0085em',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const WarningText = styled2.span({
	marginLeft: token('space.100', '8px'),
});
// @ts-expect-error - TS2322 - Type 'StyledComponentClass<GlyphProps, any, WithOptionalTheme<GlyphProps, any>>' is not assignable to type 'ComponentType<{ children?: ReactNode; label?: string | undefined; primaryColor?: string | undefined; }>'.
const WarningIconWrapper: ComponentType<{
	children?: ReactNode;
	label?: string;
	primaryColor?: string;
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-legacy-icons -- To migrate as part of go/ui-styling-standard
}> = styled2(WarningIcon)({
	marginRight: token('space.100', '8px'),
});
